<template>
  <div class="mt-4 mb-4">
    <h1>Генерация изображений</h1>

    <!-- Mode Selection -->
    <div class="mb-3">
      <label for="generationMode" class="form-label">Режим генерации:</label>
      <select
        class="form-select"
        id="generationMode"
        v-model="generationMode"
        :disabled="isLoading"
      >
        <option value="recipe">Для рецепта</option>
        <option value="freeform">Свободная форма</option>
      </select>
    </div>

    <!-- Model Selection -->
    <div class="mb-3">
      <label for="imageModelName" class="form-label">Модель генерации:</label>
      <select
        class="form-select"
        id="imageModelName"
        v-model="imageModelName"
        :disabled="isLoading"
      >
        <option value="GPT-image 2">GPT-image 2 (рекомендуется)</option>
        <option value="GPT-image 1.5">GPT-image 1.5</option>
        <option value="GPT-image 1">GPT-image 1</option>
      </select>
    </div>

    <!-- Recipe Mode Fields -->
    <div v-if="generationMode === 'recipe'" class="mb-3">
      <label for="recipeName" class="form-label">Название рецепта:</label>
      <input
        type="text"
        class="form-control"
        id="recipeName"
        v-model="recipeName"
        :disabled="isLoading"
        placeholder="например, Борщ"
      />
    </div>

    <!-- Freeform Mode Fields -->
    <div v-if="generationMode === 'freeform'">
      <!-- Generate Image Prompt -->
      <div class="mb-3">
        <label for="rawPromptText" class="form-label"
          >Текст для генерации промпта (описание):</label
        >
        <textarea
          class="form-control"
          id="rawPromptText"
          rows="15"
          v-model="rawPromptText"
          :disabled="isLoading"
          placeholder="например, Красивая тарелка супа на деревянном столе"
        ></textarea>
        <div class="d-flex justify-content-between mt-2">
          <button
            class="btn btn-outline-secondary btn-sm"
            @click="saveRawPrompt"
            :disabled="!rawPromptText.trim()"
          >
            Сохранить это описание
          </button>
          <button
            class="btn btn-secondary btn-sm"
            @click="generateImagePromptFromText"
            :disabled="isLoading || !rawPromptText.trim()"
          >
            <span
              v-if="isLoadingDallePrompt"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            {{
              isLoadingDallePrompt
                ? "Генерация промпта..."
                : "Сгенерировать промпт"
            }}
          </button>
        </div>
      </div>

      <!-- Load Saved Raw Prompts -->
      <div v-if="savedRawPrompts.length > 0" class="mb-3">
        <div class="d-flex justify-content-start">
          <button
            class="btn btn-outline-info btn-sm"
            type="button"
            @click="toggleSavedPromptsDropdown"
            :aria-expanded="showSavedPromptsDropdown.toString()"
          >
            Загрузить сохраненное описание
            <i
              :class="
                showSavedPromptsDropdown
                  ? 'bi bi-chevron-up'
                  : 'bi bi-chevron-down'
              "
            ></i>
          </button>
        </div>
        <div
          v-if="showSavedPromptsDropdown"
          class="dropdown-menu-custom list-group mt-1"
        >
          <a
            href="#"
            v-for="(p, index) in savedRawPrompts"
            :key="index"
            :draggable="true"
            @dragstart="handleDragStart(index)"
            @dragover.prevent="handleDragOver(index)"
            @drop="handleDrop(index)"
            @dragend="handleDragEnd"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            @click.prevent="applySavedRawPrompt(p)"
            :class="{
              'dragging-over':
                index === dragOverIndex && index !== draggingIndex,
            }"
          >
            <span class="saved-prompt-text flex-grow-1">
              {{ p.length > 60 ? p.substring(0, 57) + "..." : p }}
            </span>
            <button
              class="btn btn-sm btn-outline-secondary ms-2 delete-prompt-btn"
              @click.stop.prevent="deleteSavedRawPrompt(index)"
              title="Удалить описание"
            >
              <i class="bi bi-trash"></i>
            </button>
          </a>
        </div>
        <!-- Hidden select for v-model if still needed for some logic, or remove if not -->
        <select v-model="selectedSavedRawPrompt" class="d-none"></select>
      </div>

      <!-- Final Image Prompt -->
      <div class="mb-3">
        <label for="finalImagePrompt" class="form-label"
          >Финальный промпт (можно редактировать):</label
        >
        <textarea
          class="form-control"
          id="finalImagePrompt"
          rows="7"
          v-model="finalImagePrompt"
          :disabled="isLoading"
        ></textarea>
      </div>
    </div>

    <!-- Generate Image Button -->
    <button
      class="btn btn-primary mb-3"
      @click="handleGenerateImage"
      :disabled="isGenerateButtonDisabled"
    >
      <span
        v-if="isLoadingImage"
        class="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      ></span>
      {{
        isLoadingImage
          ? "Генерация изображения..."
          : "Сгенерировать изображение"
      }}
    </button>

    <!-- Error Display -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- URL field -->
    <div class="mt-3">
      <label for="imageUrl" class="form-label">URL изображения:</label>
      <input
        type="text"
        class="form-control"
        id="imageUrl"
        v-model="generatedImageUrl"
      />
    </div>

    <!-- File Name Input (appears when image is generated) -->
    <div v-if="generatedImageUrl" class="mt-3 mb-3">
      <label for="fileName" class="form-label"
        >Имя файла (без расширения):</label
      >
      <input
        type="text"
        class="form-control"
        id="fileName"
        v-model="fileName"
      />
    </div>

    <!-- Generated Image Display -->
    <div v-if="generatedImageUrl" class="mt-3 text-center">
      <h5>Сгенерированное изображение:</h5>
      <img
        :src="generatedImageUrl"
        class="img-fluid rounded"
        alt="Generated by GPT Image"
        style="max-height: 512px; border: 1px solid #ccc"
      />
      <p class="mt-2">
        <small
          >URL:
          <a :href="generatedImageUrl" target="_blank" rel="noopener noreferrer"
            >Открыть в новой вкладке</a
          ></small
        >
      </p>

      <!-- Save to disk and send to S3 buttons -->
      <div class="mt-2">
        <!-- Save to disk -->
        <button class="btn btn-success me-2" @click="downloadImage">
          Сохранить на диск
        </button>

        <!-- Send to S3 -->
        <button class="btn btn-info" @click="sendToS3" :disabled="isSavingToS3">
          <span
            v-if="isSavingToS3"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          {{ isSavingToS3 ? "Сохранение на S3..." : "Отправить на S3" }}
        </button>
      </div>
      <div v-if="s3Url" class="alert alert-success mt-4" role="alert">
        Изображение сохранено в S3: {{ s3Url }}
      </div>
      <div v-if="s3Error" class="alert alert-danger mt-4" role="alert">
        Ошибка сохранения в S3: {{ s3Error }}
      </div>
    </div>
  </div>
</template>

<script>
const CMD_IMAGE_GENERATE_CUSTOM = "image.generate.custom";

// Generate image prompt from text
// The command name is: image.generate.prompt
const CMD_IMAGE_PROMPT_FROM_TEXT = "image.generate.prompt";

// Generate an image by recipe name and return its URL.
// This command is used by the image generator to generate an image by a recipe name.
// The response is the URL of the generated image.
// The command name is: image.generate.url/{dishName}/{model}
const CMD_IMAGE_GENERATE_FOR_RECIPE = "image.generate.url";

// Save an image to S3
const CMD_IMAGE_SAVE_S3 = "image.s3.save";

// Delete an image from S3
const CMD_IMAGE_DELETE_S3 = "image.s3.del";

const LOCALSTORAGE_KEY_SAVED_RAW_PROMPTS = "cooksy_saved_raw_prompts";

export default {
  name: "ImageGenerator",
  data() {
    return {
      generationMode: "recipe", // 'recipe' or 'freeform'
      imageModelName: "GPT-image 2",
      recipeName: "",
      rawPromptText: "",
      finalImagePrompt: "",
      fileName: "",
      generatedImageUrl: null,
      s3Url: null,
      isLoading: false, // General loading state (can be split further)
      isLoadingDallePrompt: false,
      isLoadingImage: false,
      isSavingToS3: false,
      error: null,
      s3Error: null,
      savedRawPrompts: [],
      selectedSavedRawPrompt: "",
      showSavedPromptsDropdown: false, // Controls visibility of the custom dropdown
      draggingIndex: null, // Index of the item being dragged
      dragOverIndex: null, // Index of the item currently being dragged over
    };
  },

  /**
   * This lifecycle hook is called before the component is created.
   * It allows this component to admin only by sending a command to check if
   * the user has admin rights. If the user does not have admin rights, it
   * redirects them to the home page.
   */
  async beforeCreate() {
    try {
      // Check if the user has admin rights
      await this.sendCmd("login.access-admin");
    } catch (err) {
      // If the user does not have admin rights, redirect them to the home page
      window.location.href = "/";
    }
    // Load saved raw prompts on component creation
    this.loadSavedRawPrompts();
    if (this.savedRawPrompts.length > 0 && !this.rawPromptText) {
      // this.rawPromptText = this.savedRawPrompts[0]; // Optionally pre-fill with the first saved prompt
    }
  },

  computed: {
    /**
     * Whether the "Generate" button should be disabled.
     * @returns {boolean}
     */
    isGenerateButtonDisabled() {
      // If there is a loading operation going on, disable the button
      if (this.isLoading) return true;

      // If we are in recipe mode, disable the button if the recipe name is blank
      if (this.generationMode === "recipe") {
        return !this.recipeName.trim();
      }

      // If we are in freeform mode, disable the button if the prompt is blank
      if (this.generationMode === "freeform") {
        return !this.finalImagePrompt.trim();
      }

      // If the generation mode is not recognized, disable the button
      return true;
    },
  },

  methods: {
    /**
     * Resets the state of the component to its initial values.
     * This is used to clean up after a generation or saving operation.
     */
    resetState() {
      // Reset all state variables to their initial values
      this.generatedImageUrl = null;
      this.s3Url = null;
      this.error = null;
      this.fileName = "";
      this.s3Error = null;
    },

    loadSavedRawPrompts() {
      const prompts = localStorage.getItem(LOCALSTORAGE_KEY_SAVED_RAW_PROMPTS);
      if (prompts) {
        this.savedRawPrompts = JSON.parse(prompts);
      }
    },

    saveRawPrompt() {
      if (!this.rawPromptText.trim()) return;
      // Avoid duplicates
      if (!this.savedRawPrompts.includes(this.rawPromptText.trim())) {
        this.savedRawPrompts.unshift(this.rawPromptText.trim()); // Add to the beginning
        // Limit to a reasonable number, e.g., 20 most recent
        if (this.savedRawPrompts.length > 20) {
          this.savedRawPrompts.pop();
        }
        localStorage.setItem(
          LOCALSTORAGE_KEY_SAVED_RAW_PROMPTS,
          JSON.stringify(this.savedRawPrompts)
        );
        this.selectedSavedRawPrompt = this.rawPromptText.trim(); // Select the newly saved one
      } else {
        // Optionally, notify user that prompt is already saved or move it to top
        console.log("Prompt already saved.");
      }
    },

    applySavedRawPrompt(promptText) {
      if (promptText) {
        this.rawPromptText = promptText;
        this.selectedSavedRawPrompt = promptText;
      }
    },

    deleteSavedRawPrompt(index) {
      const promptToDelete = this.savedRawPrompts[index];
      this.savedRawPrompts.splice(index, 1);
      localStorage.setItem(
        LOCALSTORAGE_KEY_SAVED_RAW_PROMPTS,
        JSON.stringify(this.savedRawPrompts)
      );
      // If the deleted prompt was selected or currently in rawPromptText, clear them
      if (this.selectedSavedRawPrompt === promptToDelete) {
        this.selectedSavedRawPrompt = "";
      }
      // No need to clear rawPromptText, user might want to keep editing a version of it
    },

    toggleSavedPromptsDropdown() {
      this.showSavedPromptsDropdown = !this.showSavedPromptsDropdown;
    },

    handleDragStart(index) {
      this.draggingIndex = index;
      // Optional: Add a class to the dragged item for visual feedback
      // event.dataTransfer.effectAllowed = 'move';
    },
    handleDragOver(index) {
      // event.preventDefault(); // Already done with .prevent modifier
      if (index !== this.draggingIndex) {
        this.dragOverIndex = index;
      }
    },
    handleDrop(targetIndex) {
      if (this.draggingIndex === null || this.draggingIndex === targetIndex) {
        this.dragOverIndex = null;
        return;
      }

      const itemToMove = this.savedRawPrompts.splice(this.draggingIndex, 1)[0];
      this.savedRawPrompts.splice(targetIndex, 0, itemToMove);

      localStorage.setItem(
        LOCALSTORAGE_KEY_SAVED_RAW_PROMPTS,
        JSON.stringify(this.savedRawPrompts)
      );

      this.dragOverIndex = null;
      // draggingIndex will be reset in handleDragEnd
    },
    handleDragEnd() {
      this.draggingIndex = null;
      this.dragOverIndex = null;
      // Optional: Remove any visual feedback classes
    },

    /**
     * Generates an image prompt from the text in the "rawPromptText" field.
     * This is called when the user clicks the "Generate prompt from text" button.
     * @returns {Promise<void>}
     */
    async generateImagePromptFromText() {
      if (!this.rawPromptText.trim()) {
        this.error = "Текст для генерации промпта не может быть пустым.";
        return;
      }
      this.isLoading = true;
      this.isLoadingDallePrompt = true;
      this.resetState();

      try {
        // Send command with the text in the "rawPromptText" field
        const command = `${CMD_IMAGE_PROMPT_FROM_TEXT}/${this.imageModelName}`;
        this.finalImagePrompt = await this.sendCmd(command, this.rawPromptText);
      } catch (err) {
        console.error("Error generating image prompt from text:", err);
        this.error = `Ошибка генерации промпта: ${
          err.message || "Неизвестная ошибка"
        }`;
      } finally {
        this.isLoading = false;
        this.isLoadingDallePrompt = false;
      }
    },

    /**
     * Generates an image using the GPT Image model.
     * @returns {Promise<void>}
     */
    async handleGenerateImage() {
      this.isLoading = true;
      this.isLoadingImage = true;
      this.resetState();

      // Determine the command to send based on the generation mode
      let command = "";
      let payload = null;
      if (this.generationMode === "recipe") {
        // Generation mode is recipe
        if (!this.recipeName.trim()) {
          this.error = "Название рецепта не может быть пустым.";
          this.isLoading = false;
          this.isLoadingImage = false;
          return;
        }
        // Use the recipe name as the payload and the recipe name and model name as the command
        command = `${CMD_IMAGE_GENERATE_FOR_RECIPE}/${this.recipeName}/${this.imageModelName}`;
      } else {
        // Generation mode is freeform
        if (!this.finalImagePrompt.trim()) {
          this.error = "Финальный промпт не может быть пустым.";
          this.isLoading = false;
          this.isLoadingImage = false;
          return;
        }
        // Use the final prompt as the payload and the model name as the command
        command = `${CMD_IMAGE_GENERATE_CUSTOM}/${this.imageModelName}`;
        payload = this.finalImagePrompt;
      }

      try {
        // Send command with timeout 120000 ms (2 minutes)
        this.generatedImageUrl = await this.sendCmd(command, payload, 120000);
        // If generation was for a recipe and filename is not yet set, populate it
        if (
          this.generationMode === "recipe" &&
          this.recipeName.trim() &&
          !this.fileName.trim()
        ) {
          this.fileName = this.recipeName;
        }
      } catch (e) {
        try {
          const err = JSON.parse(e).error;
          console.error("Error generating image:", err);
          this.error = `Ошибка генерации изображения: ${
            err.message || "Неизвестная ошибка"
          }`;
        } catch (err) {
          console.error("Error generating image:", err);
        }
      } finally {
        this.isLoading = false;
        this.isLoadingImage = false;
      }
    },

    /**
     * Downloads the generated image as a file.
     *
     * @returns {Promise<void>}
     */
    async downloadImage() {
      if (!this.generatedImageUrl) return;

      this.isLoading = true; // Indicate loading for download process
      this.error = null;

      try {
        const desiredFileName = this.fileName.trim() || "generated_image";
        // Generate a temporary name for S3 staging
        const temporaryS3Name = `tmp-${Date.now()}-${desiredFileName}`;
        console.log(
          "Client: temporaryS3Name for S3 operations:",
          temporaryS3Name
        );

        // 1. Ask server to save the OpenAI image to S3 under a temporary name
        //    The temporaryS3Name is sent as part of the command path and should not be URL-encoded here.
        const saveCmd = `${CMD_IMAGE_SAVE_S3}/${temporaryS3Name}`;
        console.log("Client: calling saveCmd:", saveCmd);
        await this.sendCmd(saveCmd, this.generatedImageUrl);
        console.log("Client: saveCmd finished");

        // 2. Now that the image is in S3, construct its accessible URL
        //    (assuming your image.get command can serve it from S3, or you have direct S3 URLs)
        //    This might need adjustment based on how your S3 files are served.
        //    For now, let's assume image.get can fetch it.
        // The temporaryS3Name is the base name without extension.
        // encodeURIComponent is crucial for names with spaces or special characters.
        const encodedTemporaryS3Name = encodeURIComponent(temporaryS3Name);
        const s3FileUrl = `${this.$globalState.apiUrl}/image.get/${encodedTemporaryS3Name}`;
        console.log("Client: s3FileUrl for download:", s3FileUrl);

        // 3. Fetch the image from your server (which gets it from S3) as a blob
        const response = await fetch(s3FileUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch image from server: ${response.status} ${response.statusText}`
          );
        }
        const imageBlob = await response.blob();

        // 4. Create an object URL from the blob and trigger download
        const objectURL = URL.createObjectURL(imageBlob);
        const link = document.createElement("a");
        link.href = objectURL;
        link.download = desiredFileName + ".png";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectURL); // Clean up the object URL

        // Attempt to delete the temporary file from S3 after a short delay
        const deleteDelay = 5000; // 5 seconds delay
        console.log(
          `Client: Image download initiated. Scheduling deletion of temporary file ${temporaryS3Name} in ${deleteDelay}ms.`
        );

        setTimeout(async () => {
          try {
            // Send the base temporary name to the delete command
            await this.sendCmd(
              `${CMD_IMAGE_DELETE_S3}/${temporaryS3Name}`,
              null
            );
            console.log(
              `ImageGenerator: Attempted deletion of temporary file ${temporaryS3Name} from S3.`
            );
          } catch (deleteErr) {
            console.error(
              `ImageGenerator: Failed to delete temporary file ${temporaryS3Name} from S3:`,
              deleteErr
            );
          }
        }, deleteDelay);
      } catch (err) {
        console.error("Error during download process:", err);
        this.error = `Ошибка скачивания изображения: ${
          err.message || "Неизвестная ошибка"
        }`;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Saves the generated image to S3.
     * @returns {Promise<void>}
     *
     * This function sends a command to the server to save the generated image
     * to S3. The command includes the recipe name and the generated image URL.
     * The server will use this information to save the image to S3.
     *
     * If the command succeeds, this function sets the s3Url property to the
     * recipe name with the .png extension.
     *
     * If the command fails, this function sets the s3Error property to an error
     * message.
     */
    async sendToS3() {
      if (!this.generatedImageUrl) return;
      this.isSavingToS3 = true;
      this.s3Error = null;
      this.s3Url = null;

      try {
        // Use the fileName field for S3, fallback to recipeName if fileName is empty and mode is recipe
        let s3ObjectName = this.fileName.trim();
        if (
          !s3ObjectName &&
          this.generationMode === "recipe" &&
          this.recipeName.trim()
        ) {
          s3ObjectName = this.recipeName.trim();
        }

        if (s3ObjectName) {
          // Send command with recipe name and generated image URL
          const command = `${CMD_IMAGE_SAVE_S3}/${s3ObjectName}`;
          await this.sendCmd(command, this.generatedImageUrl);
          this.s3Url = s3ObjectName + ".png";
        } else {
          this.s3Error = "Название изображения не может быть пустым.";
        }
      } catch (err) {
        console.error("Error saving to S3:", err);
        this.s3Error = `Ошибка сохранения в S3: ${
          err.message || "Неизвестная ошибка"
        }`;
      } finally {
        this.isSavingToS3 = false;
      }
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 800px;
}
.saved-prompt-text:hover {
  text-decoration: underline;
}
.dropdown-menu-custom {
  display: block;
  position: static; 
  border: 1px solid var(--bs-border-color-translucent, #dee2e6);
  border-radius: var(--bs-border-radius, 0.375rem);
  padding: 0;
}

.delete-prompt-btn {
  /* Ensure the delete button is small and doesn't take too much space */
  padding: 0.1rem 0.3rem;
  font-size: 0.75rem;
}
.dragging-over {
  /* Visual feedback for the drop target */
  border-top: 2px solid var(--bs-info);
}
</style>
